import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import api from '../../utils/api';

const CreateOrganizer = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    conferenceName: ''
  });
  const [loading, setLoading] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setFieldErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneratedCredentials(null);
    setSuccessMessage('');
    setError('');
    setFieldErrors({});

    // Client-side validations
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please resolve all validation errors before submitting');
      setLoading(false);
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.border-red-500');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorEl.focus?.();
        }
      }, 100);
      return;
    }

    try {
      const res = await api.post('/admin/organizers', formData);
      if (res.data.success) {
        setGeneratedCredentials(res.data.data.credentials);
        setSuccessMessage(res.data.message); // Contains the auto-email confirmation
        setFormData({ name: '', email: '', phone: '', organization: '', conferenceName: '' }); // Reset form
      }
    } catch (err) {
      console.error('Error creating organizer:', err);
      setError(err.response?.data?.message || 'Error creating organizer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create New Organizer</h1>
            <p className="mt-2 text-gray-600">Provision a new organizer account and auto-generate credentials.</p>
          </div>
          <Button onClick={() => navigate('/admin/dashboard')} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>

        <Card className="mb-6 shadow-md border-t-4 border-t-blue-600">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2">
            <Input label="Full Name" name="name" value={formData.name} onChange={handleInputChange} required error={fieldErrors.name} />
            <Input label="Email Address" type="email" name="email" value={formData.email} onChange={handleInputChange} required error={fieldErrors.email} />
            <Input label="Phone Number" name="phone" value={formData.phone} onChange={handleInputChange} required error={fieldErrors.phone} />
            <Input label="Organization (Optional)" name="organization" value={formData.organization} onChange={handleInputChange} error={fieldErrors.organization} />
            <div className="md:col-span-2">
              <Input label="Conference/Event Name (Optional)" name="conferenceName" value={formData.conferenceName} onChange={handleInputChange} error={fieldErrors.conferenceName} />
            </div>
            
            {error && (
              <div className="md:col-span-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}

            <div className="md:col-span-2 mt-4">
              <Button type="submit" variant="primary" disabled={loading} className="w-full sm:w-auto px-8">
                {loading ? 'Creating Account & Sending Email...' : 'Create Organizer Account'}
              </Button>
            </div>
          </form>
        </Card>

        {generatedCredentials && (
          <Card className="mt-8 bg-emerald-50 border-emerald-200 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-bold text-emerald-800">Account Created Successfully</h3>
                <p className="mt-1 text-sm text-emerald-700">{successMessage}</p>
                
                <div className="mt-6 bg-white rounded-lg border border-emerald-100 p-4">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Generated Credentials Reference</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Username</p>
                      <p className="text-lg font-mono font-bold text-gray-900 mt-1">{generatedCredentials.username}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Password</p>
                      <p className="text-lg font-mono font-bold text-gray-900 mt-1">{generatedCredentials.password}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-amber-600 font-medium bg-amber-50 inline-block px-2 py-1 rounded">
                    ⚠️ Note: The password above will not be shown again once you leave this page.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default CreateOrganizer;
